import { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation, Trans } from 'react-i18next'
import { useFormikContext } from 'formik'
import styled from 'styled-components'
import moment from 'moment'

import { Row, FieldInput, FieldDatePicker, FieldDropdown, Group } from '@/components/form'
import formatCurrency from '@/utils/format-currency'
import { colors, fontWeights } from '@/styles'
import DepositAccountField from './deposit-account'

const SubHeader = styled.p`
  font-size: 15px;
  font-weight: ${fontWeights.medium};
  padding-bottom: 20px;
  margin: 0px;
  text-align: left;
  width: 100%;
`

const CheckBoxContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`

const CheckBox = styled(FieldInput)`
  background: ${colors.white};
  border: 1px solid ${colors.grey80};
  border-radius: 4px;
  max-width: 16px;
  max-height: 16px;
  min-width: 16px !important;
  min-height: 16px;
  width: 16px !important;
  height: 16px !important;
  background-size: 8px 8px !important;
  margin-top: 4px;
`

const CheckBoxText = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
`

const Bold = styled.span`
  font-weight: bold;
`

const Error = styled.p`
  color: ${colors.red64};
  font-size: 12px;
  line-height: 16px;
`

const PadFields = ({ accountData, contractPrice, contractId }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { values, setFieldValue, errors, submitCount, touched } = useFormikContext()
  const isPadEnabled = accountData?.financial_info !== undefined
  const [initialPadStartDate, setInitialPadStartDate] = useState(null)

  const getRequestedAmount = useCallback(() => {
    const fees = !values.additional_fee || values.additional_fee === '' ? 0 : values.additional_fee
    return parseFloat(contractPrice) + parseFloat(fees)
  }, [values.additional_fee, contractPrice])

  const totalRequested = getRequestedAmount()

  useEffect(() => {
    const fetchDueDate = async () => {
      const { first_pad_date: startDate } = await dispatch.settlements.getFirstPadDate({
        contractId
      })
      setFieldValue('due_date', startDate)
      setInitialPadStartDate(startDate)
    }

    fetchDueDate()
  }, [setFieldValue, dispatch.settlements, contractId])

  const getStartDate = () => {
    return moment(values.due_date).format('MM-DD-YYYY')
  }

  return (
    <>
      <DepositAccountField isRequired={isPadEnabled} accountData={accountData} />
      {isPadEnabled && (
        <>
          <SubHeader>{t('l.payments.view.rent_info.title', 'Rent Info')}</SubHeader>
          <Row>
            <Group>
              <FieldInput
                id="rent_price"
                name="rent_price"
                prefix="$"
                placeholder={t(
                  'l.payments.view.request_payment_modal.rent_price_label',
                  'Rent price'
                )}
                value={contractPrice}
                disabled
                required
              />
            </Group>
          </Row>
          <Row>
            <Group>
              <FieldInput
                id="additional_fee"
                name="additional_fee"
                prefix="$"
                autoComplete="off"
                placeholder={t(
                  'l.payments.view.request_payment_modal.additional_fee_label',
                  'Additional fee'
                )}
              />
            </Group>
          </Row>
          <Row>
            <Group>
              <FieldDropdown
                id="payment_frequency"
                name="payment_frequency"
                placeholder={t(
                  'l.payments.view.request_payment_modal.payment_frequency_label',
                  'Payment frequency'
                )}
                options={[
                  {
                    value: 'monthly',
                    label: t('common.frequency_monthly', 'Monthly')
                  }
                ]}
                required
                disabled
              />
            </Group>
          </Row>
          <Row>
            <Group>
              <FieldDatePicker
                id="due_date"
                name="due_date"
                placeholder={t(
                  'l.payments.view.request_payment_modal.due_date_label',
                  'When is this payment due?'
                )}
                shouldDisableDate={date => date.isBefore(moment(initialPadStartDate))}
                disablePast
                required
              />
            </Group>
          </Row>
          <Row>
            <Group>
              <CheckBoxContainer>
                <CheckBox
                  id="agree_to_terms"
                  name="agree_to_terms"
                  type="checkbox"
                  hideError
                  required
                />
                <CheckBoxText>
                  <Trans
                    t={t}
                    i18nKey="l.payments.view.request_payment_modal.agreement_body"
                    values={{
                      padStartDate: getStartDate(),
                      formattedTotal: formatCurrency(totalRequested)
                    }}
                  >
                    I hereby acknowledge that the first payment of{' '}
                    <Bold>{`{{ formattedTotal }}`}</Bold> will be processed on{' '}
                    <Bold>{`{{ padStartDate }}`}</Bold>, provided the tenant accepts the payment
                    request before <Bold>{`{{ padStartDate }}`}</Bold>. I also acknowledge that if
                    this is the first month after the contract start date, the first payment will be
                    processed the following month.
                  </Trans>
                </CheckBoxText>
              </CheckBoxContainer>
              {errors.agree_to_terms && (submitCount > 0 || touched.agree_to_terms) && (
                <Error>
                  <Trans t={t} i18nKey="l.payments.view.request_payment_modal.agreement_error">
                    You must agree to the above terms before you can submit a PAD payment request.
                  </Trans>
                </Error>
              )}
            </Group>
          </Row>
        </>
      )}
    </>
  )
}

export default PadFields
